import { COLOR_MODE, toHex } from "../common/utility"
import { useLayoutEffect, useRef } from "react"
import { useGlobalContext } from "./globalcontext"
import { nColsHgrMagnifier, nRowsHgrMagnifier, getOverrideHiresPixels, screenBytesToCanvasPixels, screenCoordToCanvasCoord, canvasCoordToNormScreenCoord } from "./graphics"
import { drawHiresTile } from "./graphicshgr"

type MagnifyProps = {
  mainCanvas: HTMLCanvasElement | null,
  mouseLoc: number[],
  lockHgrMagnifier: boolean
}

const drawBytes = (canvas: HTMLCanvasElement, pixels: number[][]) => {
  const context = canvas.getContext("2d")
  if (!context) return

  context.fillStyle = "black"
  context.fillRect(0, 0, canvas.width, canvas.height)
  const tile = new Uint8Array(nColsHgrMagnifier * nRowsHgrMagnifier)
  for (let j = 0; j < nRowsHgrMagnifier; j++) {
    for (let i = 0; i < nColsHgrMagnifier; i++) {
      tile[nColsHgrMagnifier * j + i] = pixels[j][i + 1]
    }
  }
  context.imageSmoothingEnabled = false
  const addr = pixels[0][0]
  const isEven = addr % 2 === 0
  drawHiresTile(context, tile, COLOR_MODE.NOFRINGE,
    nRowsHgrMagnifier, 0, 0, 11, isEven)
  context.beginPath()
  const nPixels = 7 * nColsHgrMagnifier
  for (let i = 1; i < nPixels; i++) {
    const x = Math.round((canvas.width / nPixels) * i + 0.5) - 0.5
    context.moveTo(x, 0)
    context.lineTo(x, canvas.height)
  }
  for (let i = 1; i < nRowsHgrMagnifier; i++) {
    const y = Math.round((canvas.height / nRowsHgrMagnifier) * i + 0.5) - 0.5
    context.moveTo(0, y)
    context.lineTo(canvas.width, y)
  }
  context.strokeStyle = "#888"
  context.stroke()
}

const HgrInfoCanvas = ({ pixels }: { pixels: number[][] }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  useLayoutEffect(() => {
    if (canvasRef.current) drawBytes(canvasRef.current, pixels)
  }, [pixels])

  return <canvas ref={canvasRef} id="hgr-info-canvas"
    style={{ border: "2px solid red" }}
    width={`${77 * nColsHgrMagnifier}pt`} height={`${11 * nRowsHgrMagnifier}pt`} />
}

const HgrMagnifier = (props: MagnifyProps) => {
  const { updateHgrMagnifier: updateHgrMagnifier, setUpdateHgrMagnifier: setUpdateHgrMagnifier,
    hgrMagnifierLoc: hgrMagnifierLoc, setHgrMagnifierLoc: setHgrMagnifierLoc,
    setLockHgrMagnifier } = useGlobalContext()
  const wasLockedRef = useRef(props.lockHgrMagnifier)

  let x = 0, y = 0
  if (props.mainCanvas) {
    const nHalf = nRowsHgrMagnifier / 2
    let [cx, cy] = canvasCoordToNormScreenCoord(props.mainCanvas, props.mouseLoc[0] - 26, props.mouseLoc[1] - 21)
    cx = Math.floor(cx * 280)
    cy = Math.floor(cy * 192)
    // Make sure the showHgrMagnifier doesn't go off the edge of the screen.
    // Also shift it to the left so it falls more naturally on an
    // HGR screen byte boundary.
    x = Math.max(Math.min(Math.min(Math.max(Math.floor(cx / 7), 0), 40 - nColsHgrMagnifier), 40), 0)
    y = Math.max(Math.min(Math.max(nHalf - 1, Math.min(cy, 191 - nHalf)) - (nHalf - 1), 192), 0)
  }

  useLayoutEffect(() => {
    if (props.lockHgrMagnifier && !wasLockedRef.current && props.mainCanvas) {
      setHgrMagnifierLoc([x, y])
    }
    wasLockedRef.current = props.lockHgrMagnifier
  }, [x, y, props.lockHgrMagnifier, props.mainCanvas, setHgrMagnifierLoc])

  useLayoutEffect(() => {
    if (updateHgrMagnifier) {
      setUpdateHgrMagnifier(false)
      // The memory table has already supplied the location for this lock.
      wasLockedRef.current = true
      setLockHgrMagnifier(true)
    }
  }, [updateHgrMagnifier, setUpdateHgrMagnifier, setLockHgrMagnifier])

  if (!props.mainCanvas) return <></>

  // Keep using the table's location while its lock request takes effect.
  const useSavedLocation = props.lockHgrMagnifier || updateHgrMagnifier
  const displayX = (useSavedLocation && hgrMagnifierLoc[0] >= 0)
    ? Math.max(Math.min(hgrMagnifierLoc[0], 40), 0)
    : Math.max(Math.min(x, 40), 0)
  const displayY = (useSavedLocation && hgrMagnifierLoc[1] >= 0)
    ? Math.max(Math.min(hgrMagnifierLoc[1], 192), 0)
    : Math.max(Math.min(y, 192), 0)

  const pixels = getOverrideHiresPixels(displayX, displayY)
  if (!pixels) return <></>
  const pixelText = pixels.map((line: Array<number>, i) => {
    return <div key={i}>
      {`${toHex(line[0])}: ${line.slice(1, nColsHgrMagnifier + 1).map((value) => toHex(value, 2)).join(" ")}`}
    </div>
  })
  const [dx, dy] = screenBytesToCanvasPixels(props.mainCanvas, nColsHgrMagnifier, nRowsHgrMagnifier)
  const col = 7 * displayX
  const row = displayY
  let [xPos, yPos] = screenCoordToCanvasCoord(props.mainCanvas, col, row)
  xPos -= 12
  yPos -= 12
  return <div className="hgr-view flex-row"
    style={{ left: `${xPos}px`, top: `${yPos}px` }}>
    <div className="hgr-view-box" style={{ width: `${dx}px`, height: `${dy}px` }}>&nbsp;</div>
    <div className="hgr-view-text">{pixelText}</div>
    <HgrInfoCanvas pixels={pixels} />
  </div>

}

export default HgrMagnifier
